/**
 * MSAL authentication — browser PKCE flow.
 *
 * The user signs in to Entra directly from the SPA (no SWA EasyAuth). MSAL
 * handles redirect, token caching (sessionStorage), and silent renewal.
 *
 * Access tokens are scoped to the Flow API app registration (Flow.User).
 * The Function App validates the token's audience + issuer before
 * serving any /api/* request.
 */
import {
  LogLevel,
  PublicClientApplication,
  type Configuration,
} from "@azure/msal-browser";

const tenantId = import.meta.env.VITE_AAD_TENANT_ID ?? "be448759-daa6-4338-a498-d95f7144c528";
const clientId = import.meta.env.VITE_AAD_CLIENT_ID ?? "83c8c42c-c7dc-4690-aca5-9701e86ddf0b";

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: `${window.location.origin}/`,
    postLogoutRedirectUri: `${window.location.origin}/`,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage keeps the token tied to the browser tab — closing
    // the tab kills the session, which is the HIPAA-friendlier default.
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) {
          console.error("[msal]", message);
        }
      },
      piiLoggingEnabled: false,
      logLevel: LogLevel.Warning,
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Scopes requested when acquiring an access token for the Flow API.
// The audience claim on the resulting token equals `api://{clientId}`,
// which is what the Function App's JWT validator checks against
// FlowSettings.entra_app_id.
export const apiScopes = {
  scopes: [`api://${clientId}/Flow.User`],
};

export async function acquireAccessToken(): Promise<string> {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error("not_signed_in");
  }
  const response = await msalInstance.acquireTokenSilent({
    ...apiScopes,
    account,
  });
  return response.accessToken;
}
